import { CalendarEvent } from '@demind-inc/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { TeamDayMemberItem, TeamDayRes, teamApi } from '../api';

interface UseTeamDayParams {
  userId: string;
  teamId?: string;
  date?: string;
}

export interface TeamDayMember extends Omit<TeamDayMemberItem, 'events' | 'phases'> {
  events: CalendarEvent[];
  phases?: unknown;
}

export interface TeamDayResult {
  date: string;
  members: TeamDayMember[];
  hasFetchedEnergy: boolean;
}

export const TEAM_DAY_QUERY_KEY = ['lifestack.team.day'];

const TEAM_DAY_CACHE_TIME_MS = 1000 * 60 * 60;
const TEAM_DAY_STALE_TIME_MS = 15 * 1000;

const getTeamDayQueryKey = ({ userId, teamId, date }: UseTeamDayParams) =>
  [...TEAM_DAY_QUERY_KEY, { userId, teamId, date }] as const;

const getTeamDayMemberCacheKey = (member: Pick<TeamDayMember, 'teamMemberId' | 'userId'>) => {
  return member.teamMemberId || member.userId;
};

const buildCachedEnergyLookup = (cachedTeamDay?: TeamDayResult) => {
  if (!cachedTeamDay?.hasFetchedEnergy) {
    return {};
  }

  return cachedTeamDay.members.reduce(
    (lookup, member) => {
      lookup[getTeamDayMemberCacheKey(member)] = {
        isEnergyShared: member.isEnergyShared,
        phases: member.phases,
      };

      return lookup;
    },
    {} as Record<string, Pick<TeamDayMember, 'isEnergyShared' | 'phases'>>
  );
};

const normalizeTeamDayResult = ({
  data,
  cachedTeamDay,
  didFetchEnergy,
}: {
  data: TeamDayRes;
  cachedTeamDay?: TeamDayResult;
  didFetchEnergy: boolean;
}) => {
  const cachedEnergyLookup = buildCachedEnergyLookup(cachedTeamDay);

  return {
    date: data.date,
    hasFetchedEnergy: didFetchEnergy || Boolean(cachedTeamDay?.hasFetchedEnergy),
    members: (data.members ?? []).map((member) => {
      const cachedEnergy = cachedEnergyLookup[getTeamDayMemberCacheKey(member)];
      const resolvedIsEnergyShared = didFetchEnergy
        ? member.isEnergyShared
        : cachedEnergy?.isEnergyShared ?? member.isEnergyShared;

      return {
        ...member,
        events: (member.events ?? []) as CalendarEvent[],
        isEnergyShared: resolvedIsEnergyShared,
        phases: didFetchEnergy
          ? member.phases
          : resolvedIsEnergyShared
            ? (cachedEnergy?.phases ?? member.phases)
            : undefined,
      };
    }),
  } as TeamDayResult;
};

export const useTeamDay = ({ userId, teamId, date }: UseTeamDayParams) => {
  const queryClient = useQueryClient();
  const teamDayQueryKey = getTeamDayQueryKey({ userId, teamId, date });

  const teamDayQuery = useQuery({
    queryKey: teamDayQueryKey,
    queryFn: () => {
      const cachedTeamDay = queryClient.getQueryData<TeamDayResult>(teamDayQueryKey);
      const teamDayQueryState = queryClient.getQueryState(teamDayQueryKey);
      const shouldFetchEnergy =
        !cachedTeamDay?.hasFetchedEnergy || Boolean(teamDayQueryState?.isInvalidated);

      return teamApi.getTeamDay(teamId!, date!, shouldFetchEnergy).then(({ data }) => {
        return normalizeTeamDayResult({
          data,
          cachedTeamDay,
          didFetchEnergy: shouldFetchEnergy,
        });
      });
    },
    refetchOnWindowFocus: false,
    enabled: !!userId && !!teamId && !!date,
    gcTime: TEAM_DAY_CACHE_TIME_MS,
    staleTime: TEAM_DAY_STALE_TIME_MS,
  });

  return {
    teamDay: teamDayQuery.data ?? null,
    dayMembers: teamDayQuery.data?.members ?? [],
    ...teamDayQuery,
  };
};
